import React, { useEffect, useRef, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
} from "@mui/material";
import * as yup from "yup";
import { makeStyles } from "tss-react/mui";
import AddDialogStyle from "styles/application/AddDialogStyle";
import {
	generateErrorState,
	handleSort,
	handleValidateObj,
	isChrome,
} from "helpers/utils";
import { useDispatch } from "react-redux";
import DyanamicDropdown from "components/Elements/DynamicDropdown";
import { getPublishedModel } from "services/models/modelList";
import ErrorInputFieldWrapper from "components/Layouts/ErrorInputFieldWrapper";
import { showError } from "../../../redux/common/actions";
import { defaultPageSize } from "helpers/utils";
import { getModelStage } from "services/models/modelDetails/modelStages";
import { getModelZonesList } from "services/models/modelDetails/modelZones";
import TextAreaInputField from "components/Elements/TextAreaInputField";
import ColourConstants from "helpers/colourConstants";
import { getPositions } from "services/clients/sites/siteApplications/userPositions";
import { getSiteDepartmentsInService } from "services/services/serviceLists";
import { getFeedbackClassifications } from "services/clients/sites/siteApplications/feedbackClassifications";
import { getDefaultFeedbackClassifications } from "services/clients/sites/siteApplications/feedbackClassifications";
import { getFeedbackStatuses } from "services/clients/sites/siteApplications/feedbackStatuses";
import { getFeedbackPriorities } from "services/clients/sites/siteApplications/feedbackPriorities";
import { getModelAvailableAsset } from "services/models/modelDetails/modelAsset";
import { getModelIntervals } from "services/models/modelDetails/modelIntervals";
import { getModelRolesListByInterval } from "services/models/modelDetails/modelRoles";
import { getFeedbackAvailableUsers, getFeedbackTasks } from "services/feedback/feedbackdetails";
import SiteAssetRegisterDropdown from "components/Elements/SiteAssetRegisterDropdown";

// Init styled components
const ADD = AddDialogStyle();

// Yup validation schema
const schema = (modelTemplateType, requireDepartment) =>
	yup.object({
		assignPositionID: yup.string("This field must be a string").nullable(),
		siteDepartmentID: requireDepartment
			? yup.string("This field must be a string").required("This field is required")
			: yup.string("This field must be a string").nullable(),
		assignUserID: yup.string("This field must be a string").nullable(),
		feedbackClassificationID: yup
			.string("This field must be a string")
			.required("The field is required"),
		feedbackStatusID: yup
			.string("This field must be a string")
			.required("The field is required"),
		feedbackPriorityID: yup
			.string("This field must be a string")
			.required("The field is required"),
		modelID: yup.string("This field is required").nullable(),
		siteAssetRegisterID: yup.string("This field is required").nullable(),
		modelVersionStageID: yup.string("This field must be a string").nullable(),
		modelVersionZoneID: yup.string("This field must be a string").nullable(),
		modelVersionTaskID: yup.string("This field must be a string").nullable(),
		modelVersionIntervalID: yup.string("This field must be a string").nullable(),
		modelVersionRoleID: yup.string("This field must be a string").nullable(),
		benefit: yup
			.string("This field must be a string")
			.required("The field is required"),
		changeRequired: yup
			.string("This field is required")
			.required("The field is required"),
	});

const useStyles = makeStyles()((theme) => ({
	dialogContent: {
		width: 500,
	},
	createButton: {
		// width: "auto",
	},
}));

// Default state schemas
const defaultErrorSchema = {
	assignPositionID: null,
	siteDepartmentID: null,
	assignUserID: null,
	feedbackClassificationID: null,
	feedbackStatusID: null,
	feedbackPriorityID: null,
	benefit: null,
	modelID: null,
	siteAssetRegisterID: null,
	modelVersionStageID: null,
	modelVersionZoneID: null,
	modelVersionTaskID: null,
	modelVersionIntervalID: null,
	modelVersionRoleID: null,
	changeRequired: null,
};
const defaultStateSchema = {
	assignPositionID: {},
	siteDepartmentID: {},
	assignUserID: {},
	feedbackClassificationID: {},
	feedbackStatusID: {},
	feedbackPriorityID: {},
	benefit: "",
	modelID: {},
	siteAssetRegisterID: {},
	modelVersionStageID: {},
	modelVersionZoneID: {},
	modelVersionTaskID: {},
	modelVersionIntervalID: {},
	modelVersionRoleID: {},
	changeRequired: "",
};

function AddNewFeedbackDetail({
	open,
	closeHandler,
	siteAppId,
	title,
	createProcessHandler,
	customCaptions,
	setSearchQuery,
	fetchData,
	setDataForFetchingFeedback,
	siteID,
	showAssetReferences,
	feedbackRequireDepartment = false,
}) {
	// Init hooks
	const { classes, cx } = useStyles();
	const dispatch = useDispatch();

	// Init state
	const [availableModel, setAvailableModel] = useState([]);
	const [isUpdating, setIsUpdating] = useState(false);
	const [input, setInput] = useState(defaultStateSchema);
	const [errors, setErrors] = useState(defaultErrorSchema);
	const [positionUsers, setPositionUsers] = useState([]);
	const [modelFocus, setModelFocus] = useState(true);
	const [intervals, setIntervals] = useState([]);
	const [roles, setRoles] = useState([]);
	const [stages, setStages] = useState([]);
	const [zones, setZones] = useState([]);
	const [departments, setDepartments] = useState([]);
	const departmentsRef = useRef(departments);
	useEffect(() => { departmentsRef.current = departments; }, [departments]);

	useEffect(() => {
		if (open) {
			async function fetchSiteApp() {
				setIsUpdating(true);
				try {
					const [defaultsResponse, deptResponse] = await Promise.all([
						getDefaultFeedbackClassifications(siteAppId),
						getSiteDepartmentsInService(siteID),
					]);
					if (defaultsResponse.status) {
						setInput((prev) => ({
							...prev,
							feedbackClassificationID: {
								...defaultsResponse.data,
								id: defaultsResponse.data.defaultFeedbackClassificationID,
								name: defaultsResponse.data.defaultFeedbackClassificationName,
							},
							feedbackStatusID: {
								...defaultsResponse.data,
								id: defaultsResponse.data.defaultFeedbackStatusID,
								name: defaultsResponse.data.defaultFeedbackStatusName,
							},
							feedbackPriorityID: {
								...defaultsResponse.data,
								id: defaultsResponse.data.defaultFeedbackPriorityID,
								name: defaultsResponse.data.defaultFeedbackPriorityName,
							},
						}));
					}
					if (deptResponse.status) {
						setDepartments(deptResponse.data);
					}
				} catch (error) {}
				setIsUpdating(false);
			}
			fetchSiteApp();
		}
	}, [open, siteAppId, siteID]);

	const closeOverride = () => {
		setInput(defaultStateSchema);
		setErrors(defaultErrorSchema);
		setPositionUsers([]);
		setAvailableModel([]);
		setIntervals([]);
		setRoles([]);
		setStages([]);
		setZones([]);
		setDepartments([]);
		closeHandler();
	};

	const handleCreateProcess = async () => {
		// clear search data
		setSearchQuery("");

		// Rendering spinner
		setIsUpdating(true);

		// Clearing errors before attempted create
		setErrors(defaultErrorSchema);

		const selectedSiteAssetRegisterId =
			input?.siteAssetRegisterID?.siteAssetRegisterID || null;
		const { siteAssetRegisterID, ...restInput } = input;

		// cleaned Input
		const cleanInput = {
			...restInput,
			siteAssetRegisterID: selectedSiteAssetRegisterId,
			benefit: input?.benefit,
			modelVersionStageID: input?.modelVersionStageID?.id || null,
			modelVersionZoneID: input?.modelVersionZoneID?.id || null,
			modelVersionTaskID: input?.modelVersionTaskID?.id || null,
			modelVersionIntervalID: input?.modelVersionIntervalID?.id || null,
			modelVersionRoleID: input?.modelVersionRoleID?.id || null,
			assignPositionID: input?.assignPositionID?.id || null,
			siteDepartmentID: input?.siteDepartmentID?.id || null,
			assignUserID: input?.assignUserID?.id || null,
			feedbackClassificationID: input?.feedbackClassificationID?.id || "",
			feedbackStatusID: input?.feedbackStatusID?.id || "",
			feedbackPriorityID: input?.feedbackPriorityID?.id || "",
			modelID: input?.modelID?.id || null,
			changeRequired: input?.changeRequired,
		};

		try {
			const localChecker = await handleValidateObj(
				schema(input?.modelID?.modelTemplateType, feedbackRequireDepartment),
				cleanInput
			);

			// Attempting API call if no local validaton errors
			if (!localChecker.some((el) => el.valid === false)) {
				const payload = {
					...cleanInput,
					siteAppId: siteAppId,
				};

				const newData = await createProcessHandler(payload);

				if (newData.status === true) {
					setDataForFetchingFeedback({
						pageNumber: 1,
						pageSize: defaultPageSize(),
						search: "",
						sortField: "",
						sort: "",
					});
					await fetchData();
					setIsUpdating(false);
					closeOverride();
				} else {
					setIsUpdating(false);

					dispatch(
						showError(
							newData?.data?.detail ||
								"Failed to add new " + customCaptions?.feedback
						)
					);
				}
			} else {
				// show validation errors
				const newErrors = generateErrorState(localChecker);
				setErrors({ ...errors, ...newErrors });
				setIsUpdating(false);
			}
		} catch (err) {
			setIsUpdating(false);
			setErrors({ ...errors, ...err?.response?.data?.errors });

			dispatch(showError("Failed to add new feedback"));
		}
	};

	const modelid = input?.modelID?.id;
	const modelVersionId = input?.modelID?.activeModelVersionID;
	const intervalId = input?.modelVersionIntervalID?.id;
	const stageId = input?.modelVersionStageID?.id;
	const stageHasZones = input?.modelVersionStageID?.hasZones;

	useEffect(() => {
		const fetchModelAvailableAssest = async (id) => {
			const response = await getModelAvailableAsset(id);
			if (response.status) {
				setAvailableModel(response.data);
				if (response.data.length === 1) {
					setInput((prev) => ({ ...prev, siteAssetRegisterID: response.data[0] }));
				}
			} else dispatch(showError("Could not fetch Users"));
		};
		if (modelid) {
			fetchModelAvailableAssest(modelid);
		}
	}, [modelid, dispatch]);

	useEffect(() => {
		if (!modelVersionId) return;
		const fetchIntervals = async () => {
			const response = await getModelIntervals(modelVersionId);
			if (response.status) {
				setIntervals(response.data);
				if (response.data.length === 1) {
					setInput((prev) => ({ ...prev, modelVersionIntervalID: response.data[0] }));
				}
			} else {
				dispatch(showError("Could not fetch intervals"));
			}
		};
		fetchIntervals();
	}, [modelVersionId, dispatch]);

	useEffect(() => {
		if (!intervalId) return;
		const fetchRoles = async () => {
			const response = await getModelRolesListByInterval(intervalId);
			if (response.status) {
				setRoles(response.data);
				if (response.data.length === 1) {
					setInput((prev) => ({ ...prev, modelVersionRoleID: response.data[0] }));
				}
			} else {
				dispatch(showError("Could not fetch roles"));
			}
		};
		fetchRoles();
	}, [intervalId, dispatch]);

	useEffect(() => {
		if (!modelVersionId) return;
		const fetchStages = async () => {
			const response = await getModelStage(modelVersionId);
			if (response.status) {
				setStages(response.data);
				if (response.data.length === 1) {
					setInput((prev) => ({ ...prev, modelVersionStageID: response.data[0] }));
				}
			} else {
				dispatch(showError("Could not fetch stages"));
			}
		};
		fetchStages();
	}, [modelVersionId, dispatch]);

	useEffect(() => {
		if (!stageId || !modelVersionId || stageHasZones === false) return;
		const fetchZones = async () => {
			const response = await getModelZonesList(modelVersionId);
			if (response.status) {
				setZones(response.data);
				if (response.data.length === 1) {
					setInput((prev) => ({ ...prev, modelVersionZoneID: response.data[0] }));
				}
			} else {
				dispatch(showError("Could not fetch zones"));
			}
		};
		fetchZones();
	}, [stageId, modelVersionId, stageHasZones, dispatch]);

	const positionUserId = input?.assignPositionID?.id;
	const departmentId = input?.siteDepartmentID?.id;

	useEffect(() => {
		const fetchAvailableUsers = async () => {
			const response = await getFeedbackAvailableUsers({
				siteDepartmentID: departmentId || undefined,
				positionID: positionUserId || undefined,
			});

			if (response.status) {
				const newDatas = response.data.map((d) => ({ ...d, id: d.userID }));
				setPositionUsers(newDatas);
				setInput((prev) => {
					const currentUserId = prev?.assignUserID?.id;
					const isCurrentUserInList =
						currentUserId && newDatas.some((u) => u.id === currentUserId);
					if (newDatas.length === 1) {
						const selectedUser = newDatas[0];
						const updates = { ...prev, assignUserID: selectedUser };
						if (
							selectedUser.siteDepartmentID &&
							prev?.siteDepartmentID?.id !== selectedUser.siteDepartmentID
						) {
							const dept = departmentsRef.current.find(
								(d) => d.id === selectedUser.siteDepartmentID
							);
							if (dept) updates.siteDepartmentID = dept;
						}
						return updates;
					} else if (currentUserId && !isCurrentUserInList) {
						return { ...prev, assignUserID: {} };
					}
					return prev;
				});
			} else dispatch(showError("Could not fetch Users"));
		};
		if (positionUserId || departmentId) fetchAvailableUsers();
		else setPositionUsers([]);
	}, [positionUserId, departmentId, dispatch]);

	return (
		<div>
			<Dialog
				open={open}
				onClose={closeOverride}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				className="large-application-dailog"
				disableEnforceFocus={isChrome() ? modelFocus : false}
			>
				{isUpdating ? <LinearProgress /> : null}

				<ADD.ActionContainer>
					<DialogTitle id="alert-dialog-title">
						{<ADD.HeaderText>{title}</ADD.HeaderText>}
					</DialogTitle>
					<ADD.ButtonContainer>
						<div className="modalButton">
							<ADD.CancelButton
								onClick={closeOverride}
								variant="contained"
								onFocus={(e) => {
									setModelFocus(true);
								}}
							>
								Cancel
							</ADD.CancelButton>
						</div>
						<div className="modalButton">
							<ADD.ConfirmButton
								onClick={handleCreateProcess}
								variant="contained"
								className={classes.createButton}
								disabled={isUpdating}
							>
								{title}
							</ADD.ConfirmButton>
						</div>
					</ADD.ButtonContainer>
				</ADD.ActionContainer>

				<DialogContent className={classes.dialogContent}>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.assignPositionID === null
										? null
										: errors.assignPositionID
								}
							>
								<DyanamicDropdown
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.position}
									dataHeader={[{ id: 1, name: "Name" }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={{
										...input["assignPositionID"],
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({
											...prev,
											assignPositionID: val,
											// If position cleared, clear user immediately;
											// if changed to a new value, the fetch useEffect
											// handles it conditionally
											...(!val.id ? { assignUserID: {} } : {}),
										}));
									}}
									showClear
									selectdValueToshow={"name"}
									label={customCaptions?.position}
									isError={errors.assignPositionID === null ? false : true}
									fetchData={() => getPositions(siteAppId)}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>

						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.siteDepartmentID === null
										? null
										: errors.siteDepartmentID
								}
							>
								<DyanamicDropdown
									dataSource={departments}
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.department}
									dataHeader={[
										{
											id: 1,
											name: `${customCaptions?.department ?? " Department"}`,
										},
										{
											id: 2,
											name: `${customCaptions?.location ?? "Location"}`,
										},
									]}
									showHeader
									showClear
									columns={[
										{ id: 1, name: "name" },
										{ id: 2, name: "description" },
									]}
									selectedValue={{
										...input["siteDepartmentID"],
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({
											...prev,
											siteDepartmentID: val,
										}));
									}}
									selectdValueToshow={"name"}
									label={customCaptions?.department}
									required={feedbackRequireDepartment}
									isError={errors.siteDepartmentID === null ? false : true}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.assignUserID === null ? null : errors.assignUserID
								}
							>
								<DyanamicDropdown
									dataSource={positionUsers}
									isServerSide={false}
									showClear
									width="100%"
									placeholder={"Select " + customCaptions?.user}
									dataHeader={[{ id: 1, name: "Name" }]}
									columns={[{ id: 1, name: "displayName" }]}
									selectedValue={{
										...input["assignUserID"],
										name: input?.assignUserID?.displayName,
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => {
											const updates = { ...prev, assignUserID: val };
											if (
												val?.siteDepartmentID &&
												prev?.siteDepartmentID?.id !== val.siteDepartmentID
											) {
												const dept = departmentsRef.current.find(
													(d) => d.id === val.siteDepartmentID
												);
												if (dept) updates.siteDepartmentID = dept;
											}
											return updates;
										});
									}}
									selectdValueToshow={"name"}
									label={customCaptions?.user}
									isError={errors.assignUserID === null ? false : true}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>

						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.feedbackClassificationID === null
										? null
										: errors.feedbackClassificationID
								}
							>
								<DyanamicDropdown
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.classification}
									dataHeader={[{ id: 1, name: customCaptions?.classification }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={{
										...input["feedbackClassificationID"],
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({ ...prev, feedbackClassificationID: val }));
									}}
									selectdValueToshow={"name"}
									label={customCaptions?.classification}
									isError={
										errors.feedbackClassificationID === null ? false : true
									}
									required
									fetchData={() => getFeedbackClassifications(siteAppId)}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.feedbackStatusID === null
										? null
										: errors.feedbackStatusID
								}
							>
								<DyanamicDropdown
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.feedbackStatus}
									dataHeader={[{ id: 1, name: "Name" }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={{
										...input["feedbackStatusID"],
										name: input?.feedbackStatusID?.name,
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({ ...prev, feedbackStatusID: val }));
									}}
									required
									selectdValueToshow={"name"}
									label={customCaptions?.feedbackStatus}
									isError={errors.feedbackStatusID === null ? false : true}
									fetchData={() => getFeedbackStatuses(siteAppId)}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>

						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.feedbackPriorityID === null
										? null
										: errors.feedbackPriorityID
								}
							>
								<DyanamicDropdown
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.priority}
									dataHeader={[{ id: 1, name: customCaptions?.priority }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={{
										...input["feedbackPriorityID"],
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({ ...prev, feedbackPriorityID: val }));
									}}
									selectdValueToshow={"name"}
									label={customCaptions?.priority}
									isError={errors.feedbackPriorityID === null ? false : true}
									required
									fetchData={() => getFeedbackPriorities(siteAppId)}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={errors.modelID === null ? null : errors.modelID}
							>
								<DyanamicDropdown
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.model}
									dataHeader={[
										{ id: 1, name: "Name" },
										{ id: 2, name: "Model" },
									]}
									columns={[
										{ id: 1, name: "name" },
										{ id: 2, name: "modelName" },
									]}
									showHeader
									selectedValue={{
										...input["modelID"],
										name: !input?.modelID?.name
											? ""
											: !input?.modelID?.modelName
											? input?.modelID?.name
											: input?.modelID?.name + " " + input?.modelID?.modelName,
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({
											...prev,
											modelID: val,
											siteAssetRegisterID: {},
											modelVersionStageID: {},
											modelVersionZoneID: {},
											modelVersionTaskID: {},
											modelVersionIntervalID: {},
											modelVersionRoleID: {},
										}));
										setIntervals([]);
										setRoles([]);
										setStages([]);
										setZones([]);
									}}
									showClear
									selectdValueToshow={"name"}
									label={customCaptions?.model}
									isError={errors.modelID === null ? false : true}
									fetchData={() => getPublishedModel(siteAppId)}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>

						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.siteAssetRegisterID === null
										? null
										: errors.siteAssetRegisterID
								}
							>
								<SiteAssetRegisterDropdown
									dataSource={availableModel}
									selectedValue={
										(input.siteAssetRegisterID?.id || input.siteAssetRegisterID?.siteAssetRegisterID)
											? input.siteAssetRegisterID
											: null
									}
									onChange={(val) => {
										setInput((prev) => ({ ...prev, siteAssetRegisterID: val }));
									}}
									onClear={() => {
										setInput((prev) => ({ ...prev, siteAssetRegisterID: {} }));
									}}
									customCaptions={customCaptions}
									showReferenceColumn={showAssetReferences}
									showSelectedReference={false}
									required={false}
									isError={errors.siteAssetRegisterID === null ? false : true}
									disabled={
										input?.modelID?.id === null ||
										input?.modelID?.id === undefined
									}
									label={customCaptions?.asset}
									isServerSide={false}
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.modelVersionIntervalID === null
										? null
										: errors.modelVersionIntervalID
								}
							>
								<DyanamicDropdown
									dataSource={intervals}
									isServerSide={false}
									width="100%"
									placeholder={"Select " + (customCaptions?.interval ?? "Interval")}
									dataHeader={[{ id: 1, name: customCaptions?.interval ?? "Interval" }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={input["modelVersionIntervalID"]}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({
											...prev,
											modelVersionIntervalID: val,
											modelVersionRoleID: {},
										}));
										setRoles([]);
									}}
									showClear
									selectdValueToshow="name"
									label={customCaptions?.interval ?? "Interval"}
									isError={errors.modelVersionIntervalID === null ? false : true}
									isReadOnly={
										input?.modelID?.id === null ||
										input?.modelID?.id === undefined
									}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>
						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.modelVersionStageID === null
										? null
										: errors.modelVersionStageID
								}
							>
								<DyanamicDropdown
									dataSource={stages}
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.stage}
									dataHeader={[{ id: 1, name: "Stage" }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={input["modelVersionStageID"]}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({
											...prev,
											modelVersionStageID: val,
											modelVersionZoneID: {},
											modelVersionTaskID: {},
										}));
										setZones([]);
									}}
									showClear
									selectdValueToshow="name"
									label={customCaptions?.stage}
									isError={errors.modelVersionStageID === null ? false : true}
									isReadOnly={
										input?.modelID?.id === null ||
										input?.modelID?.id === undefined
									}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.modelVersionRoleID === null
										? null
										: errors.modelVersionRoleID
								}
							>
								<DyanamicDropdown
									dataSource={roles}
									isServerSide={false}
									width="100%"
									placeholder={"Select " + (customCaptions?.role ?? "Role")}
									dataHeader={[{ id: 1, name: customCaptions?.role ?? "Role" }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={input["modelVersionRoleID"]}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({ ...prev, modelVersionRoleID: val }));
									}}
									showClear
									selectdValueToshow="name"
									label={customCaptions?.role ?? "Role"}
									isError={errors.modelVersionRoleID === null ? false : true}
									isReadOnly={
										input?.modelVersionIntervalID?.id === null ||
										input?.modelVersionIntervalID?.id === undefined
									}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>
						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.modelVersionZoneID === null
										? null
										: errors.modelVersionZoneID
								}
							>
								<DyanamicDropdown
									dataSource={zones}
									isServerSide={false}
									width="100%"
									placeholder={"Select " + customCaptions?.zone}
									dataHeader={[{ id: 1, name: "Zone" }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={input["modelVersionZoneID"]}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({
											...prev,
											modelVersionZoneID: val,
											modelVersionTaskID: {},
										}));
									}}
									showClear
									selectdValueToshow="name"
									label={customCaptions?.zone}
									isError={errors.modelVersionZoneID === null ? false : true}
									autoSelectSingleOption
									isReadOnly={
										input?.modelID?.id === null ||
										input?.modelID?.id === undefined ||
										input?.modelVersionStageID?.id === null ||
										input?.modelVersionStageID?.id === undefined ||
										input?.modelVersionStageID?.hasZones === false
									}
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.modelVersionTaskID === null
										? null
										: errors.modelVersionTaskID
								}
							>
								<DyanamicDropdown
									key={`task-${input?.modelVersionStageID?.id ?? "none"}-${input?.modelVersionZoneID?.id ?? "none"}-${input?.modelVersionIntervalID?.id ?? "none"}-${input?.modelVersionRoleID?.id ?? "none"}`}
									isServerSide={false}
									width="100%"
									placeholder={"Select " + (customCaptions?.task ?? "Task")}
									dataHeader={[
										{ id: 1, name: "Action" },
										{ id: 2, name: "Task" },
									]}
									showHeader
									columns={[
										{ id: 1, name: "actionName" },
										{ id: 2, name: "name" },
									]}
									selectedValue={input["modelVersionTaskID"]}
									handleSort={handleSort}
									onChange={(val) => {
										setInput((prev) => ({
											...prev,
											modelVersionTaskID: {
												...val,
												name: `${val.actionName ?? ""} ${val.name ?? ""}`.trim(),
											},
										}));
									}}
									showClear
									selectdValueToshow="name"
									label={customCaptions?.task ?? "Task"}
									isError={errors.modelVersionTaskID === null ? false : true}
									isReadOnly={
										input?.modelID?.id === null ||
										input?.modelID?.id === undefined ||
										input?.modelVersionStageID?.id === null ||
										input?.modelVersionStageID?.id === undefined
									}
									fetchData={() =>
										getFeedbackTasks(
											input?.modelVersionStageID?.id,
											input?.modelVersionZoneID?.id,
											input?.modelVersionIntervalID?.id,
											input?.modelVersionRoleID?.id
										)
									}
									autoSelectSingleOption
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.changeRequired === null ? null : errors.changeRequired
								}
							>
								<ADD.NameLabel>
									{`${customCaptions.changeRequired}`}
									<ADD.RequiredStar>*</ADD.RequiredStar>
								</ADD.NameLabel>

								<TextAreaInputField
									value={input?.changeRequired}
									onChange={(e) => {
										setInput((prev) => ({ ...prev, changeRequired: e.target.value }));
									}}
									minRows={5}
									style={{
										width: "100%",
										border:
											errors.changeRequired === null
												? ""
												: "1px solid " + ColourConstants.red,
										fontFamily: "Roboto Condensed",
										fontSize: "16px",
										borderRadius: "5px",
									}}
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>
						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={errors.benefit === null ? null : errors.benefit}
							>
								<ADD.NameLabel>
									{`${customCaptions.benefit}`}
									<ADD.RequiredStar>*</ADD.RequiredStar>
								</ADD.NameLabel>

								<TextAreaInputField
									value={input?.benefit}
									onChange={(e) => {
										setInput((prev) => ({ ...prev, benefit: e.target.value }));
									}}
									minRows={5}
									style={{
										width: "100%",
										border:
											errors.benefit === null
												? ""
												: "1px solid " + ColourConstants.red,
										fontFamily: "Roboto Condensed",
										fontSize: "16px",
										borderRadius: "5px",
									}}
									onBlur={() => {
										setModelFocus(false);
									}}
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
				</DialogContent>
			</Dialog>
		</div>
	);
}

export default AddNewFeedbackDetail;
